import type { FC } from 'react';
import { useEffect } from 'react';

import { useTrackedPage } from 'analytics/appInsights';
import { useFetchUserSettings } from 'api/user-settings/hooks/useFetchUserSettings';
import { AppRouter } from 'app/routes/AppRouter';
import { ErrorBoundary } from 'components/feedback/ErrorBoundary';
import { ToastMessage } from 'components/feedback/ToastMessage';
import { TopBar } from 'components/layout/topbar/TopBar';
import { FeatureFlagsMenu } from 'features/configuration/components/FeatureFlagsMenu';
import { useInitializeFeatureFlagsStore } from 'features/configuration/hooks/useInitializeFeatureFlagsStore';
import ProposalContainer from 'features/project-versioning/components/ProposalContainer';
import { FullTextSearchBar } from 'pages/projects/projectPage/selectedProject/fullTextSearch/FullTextSearchBar';
import ProjectHistoryPanelContainer from 'pages/projects/projectPage/selectedProject/projectHistory/ProjectHistoryPanelContainer';
import { useUserEmail } from 'state/appState/appStateHooks';
import { useProjectTeamMappingsActions } from 'state/projectTeamMappingState/projectTeamMappingsActions';
import { useProjectActions } from 'state/projects/projectsStore';
import { useNotificationApiActions } from 'state/signalr/useNotificationApiActions';
import { useSignalRActions } from 'state/signalr/useSignalRActions';

import './app.less';

export const App: FC = () => {
  const userEmail = useUserEmail();
  const projectActions = useProjectActions();
  const projectTeamMappingsActions = useProjectTeamMappingsActions();

  useTrackedPage(userEmail ?? 'Anonymous', 'Homepage');

  useInitializeFeatureFlagsStore();

  useEffect(() => {
    Promise.all([projectActions.GetAll(), projectTeamMappingsActions.GetAll()]).catch((reason) =>
      console.error(reason)
    );
    /* Only run once */
  }, []);

  // Fetch user settings to ensure the user settings are loaded to the store and there is no glitching
  useFetchUserSettings();

  // Ideally, we don't need to initiate the connection here and only connect when needed (i.e. lazy connect).
  // However, this causes issues for the current implementation.
  // e.g. when releasing a blueprint, it fires a REST request and then the component is listening for the signalR event.
  // But, there was no trigger to connect to signalR in those area, so the event was never received.
  // TODO: identify these use cases, and make it consistent, so that we can lazy connect to signalR.
  const signalRActions = useSignalRActions();

  // NotificationApi hub: carries almost every server-push notification DataApi used to broadcast
  // directly off its own /datahub (project/template/import/report lifecycle, grid data changes,
  // etc.) - DataHubContext now publishes them through NotificationApi instead. See
  // NotificationApiActions for what's on this connection vs SignalRActions' (which keeps only
  // request/response-style hub calls like RequestRootRows).
  useNotificationApiActions(signalRActions);

  return (
    <ErrorBoundary>
      <TopBar
        ProposalContainer={ProposalContainer}
        FeatureFlagsMenu={FeatureFlagsMenu}
        FullTextSearchBar={FullTextSearchBar}
      />
      <AppRouter />
      <ProjectHistoryPanelContainer />
      <ToastMessage />
    </ErrorBoundary>
  );
};
